import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';

import ResponsiveImage from '~/components/Common/Elements/ResponsiveImage';

import { useGetExperienceOffer } from '~/hooks/apis/experiences';

import { type ExperienceOfferItem, mapExperienceOfferItem } from '../../hooks';

interface Props {
  selectedOfferIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
  onPrimaryOfferChange?: (offer?: ExperienceOfferItem) => void;
}

export default function PassportExperienceLookup({ selectedOfferIds, onSelectionChange, onPrimaryOfferChange }: Props) {
  const selectedOfferId = selectedOfferIds[0];
  const previousSelectedOfferIdRef = useRef(selectedOfferId);
  const [offerIdInput, setOfferIdInput] = useState(selectedOfferId ?? '');
  const [searchedOfferId, setSearchedOfferId] = useState(selectedOfferId);
  const { data: experienceOffer, error, isError, isFetching } = useGetExperienceOffer(searchedOfferId);
  const selectedExperience = useMemo(
    () => (experienceOffer ? mapExperienceOfferItem(experienceOffer) : undefined),
    [experienceOffer],
  );
  const customerExperienceId = selectedExperience?.id.startsWith('led:')
    ? selectedExperience.id
    : `led:${selectedExperience?.id}`;

  useEffect(() => {
    if (selectedOfferId && selectedOfferIds.length > 1) {
      onSelectionChange([selectedOfferId]);
    }
  }, [onSelectionChange, selectedOfferId, selectedOfferIds.length]);

  useEffect(() => {
    const selectedOfferChangedExternally = previousSelectedOfferIdRef.current !== selectedOfferId;

    if (selectedOfferChangedExternally && selectedOfferId && selectedOfferId !== searchedOfferId) {
      setOfferIdInput(selectedOfferId);
      setSearchedOfferId(selectedOfferId);
    }

    previousSelectedOfferIdRef.current = selectedOfferId;
  }, [searchedOfferId, selectedOfferId]);

  useEffect(() => {
    if (!selectedExperience || !searchedOfferId) {
      return;
    }

    onPrimaryOfferChange?.(selectedExperience);

    if (selectedOfferIds.length !== 1 || selectedOfferId !== selectedExperience.id) {
      onSelectionChange([selectedExperience.id]);
    }
  }, [
    onPrimaryOfferChange,
    onSelectionChange,
    searchedOfferId,
    selectedExperience?.id,
    selectedExperience?.imageId,
    selectedExperience?.imageUrl,
    selectedExperience?.location?.latitude,
    selectedExperience?.location?.longitude,
    selectedExperience?.title,
    selectedOfferId,
    selectedOfferIds.length,
  ]);

  const handleSearch = () => {
    const nextOfferId = offerIdInput.trim();
    if (!nextOfferId || nextOfferId === searchedOfferId) {
      return;
    }

    onPrimaryOfferChange?.(undefined);
    setSearchedOfferId(nextOfferId);
  };

  return (
    <Stack spacing={1}>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSearch();
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" fontWeight="medium">
            ID
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={offerIdInput}
            onChange={(event) => setOfferIdInput(event.target.value)}
            autoComplete="off"
            placeholder="Experience offer ID"
            inputProps={{ 'aria-label': 'Experience offer ID' }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={
              !offerIdInput.trim() ||
              isFetching ||
              (offerIdInput.trim() === searchedOfferId && !isError && !!experienceOffer)
            }
            startIcon={isFetching ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Search
          </Button>
        </Stack>
      </Box>
      {isError && (
        <Alert severity="error">
          Unable to load the experience offer. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}
      {selectedExperience && !isError && (
        <Box display="flex" gap={2} alignItems="center" border={1} borderColor="divider" borderRadius={1} p={1}>
          {selectedExperience.imageUrl ? (
            <Box
              component="img"
              src={selectedExperience.imageUrl}
              alt={selectedExperience.title}
              width={120}
              height={80}
              sx={{ objectFit: 'cover' }}
            />
          ) : selectedExperience.imageId ? (
            <ResponsiveImage imageId={selectedExperience.imageId} width={120} height={80} />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" width={120} height={80} bgcolor="grey.200">
              <Typography variant="caption" color="text.secondary">
                No image
              </Typography>
            </Box>
          )}
          <Stack spacing={0.5} minWidth={0}>
            <Link
              href={`${window.configs.LUX_CUSTOMER_PORTAL}/experience/${customerExperienceId}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              variant="body2"
            >
              {selectedExperience.id}
            </Link>
            <Typography variant="body1">{selectedExperience.title}</Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
